import * as Sentry from '@sentry/nextjs';
import { v4 } from 'uuid';

import appConfig from 'app-config';

import { DEBUG } from 'src/config';
import store from 'src/stores';
import { userDataAtom } from 'src/stores/auth/atoms';
import { debugEventLogsAtom } from 'src/stores/debug/atom';
import { TUTMParams, UTMParamsAtom } from 'src/stores/utm/atoms';
import { visitorConfigAtom } from 'src/stores/visitor/atoms';
import { EVENT_TYPE, EventName, EventOption, EventType } from 'src/types/Event';
import getDeviceId from 'src/utils/device/id';
import getDeviceInfo, { isPwaMode } from 'src/utils/device/info';
import { initializeMetric } from 'src/utils/metric';
import { safeWindow } from 'src/utils/static-generation';

const service_code = appConfig.serviceCode.toUpperCase();

interface EventMessage {
  event_type: EventType;
  event_name: EventName | string;
  event_timestamp: number;
  event_uid: string;
  device_params: {
    os: 'WEB_VIEW' | 'WEB';
  };
  user_id: string | null;
  event_params: unknown;
  service_code: string;
  unique_user_key: string;
  utm_params: TUTMParams;
}

export type EventData<T = object> = {
  eventType: EventMessage['event_type'];
  eventName: EventMessage['event_name'];
  eventParams?: T;
  options?: EventOption;
};

type EventGatewayType = 'PAYMENT' | 'GENERAL';

const EVENT_GATEWAY_SEND_INTERVAL = 3_000;

/** Payment 관련 이벤트는 전용 엔드포인트로 전송해야 함.
 * @see {@link https://www.notion.so/hpcnt/API-8b5dbd04bc2f4601863a40abb0c5e460?pvs=4#32cd2c7091014968983c9cf43bfc2185}
 */
const EVENT_ENDPOINT_MAP: Record<EventGatewayType, '/' | `/${string}/`> = {
  PAYMENT: '/event-gateway/api/v1/event/payment/',
  GENERAL: '/event-gateway/api/v1/event/',
};

class EventGatewayClient {
  private static instance: EventGatewayClient;

  private origin: string | null = null;

  private eventMessageQueue: Record<EventGatewayType, EventMessage[]> = {
    GENERAL: [],
    PAYMENT: [],
  };

  private sendIntervalId: number | null = null;

  private _start() {
    this.sendIntervalId = window.setInterval(() => {
      this.sendEventMessages('GENERAL');
      this.sendEventMessages('PAYMENT');
    }, EVENT_GATEWAY_SEND_INTERVAL);
    window.addEventListener('beforeunload', () => {
      if (this.sendIntervalId) window.clearInterval(this.sendIntervalId);
      this.sendEventMessages('GENERAL', { keepalive: true });
      this.sendEventMessages('PAYMENT', { keepalive: true });
    });

    // iOS Safari 등 beforeunload가 발생하지 않는 환경에서도 이벤트 전송을 보장하기 위해
    // visibilitychange (hidden) 시점에도 큐를 flush한다.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // microtask로 지연하여 동일 visibilitychange 이벤트를 구독하는 다른 리스너들이
        // 동기적으로 큐에 이벤트를 push한 뒤 flush되도록 보장한다.
        queueMicrotask(() => {
          this.sendEventMessages('GENERAL', { keepalive: true });
          this.sendEventMessages('PAYMENT', { keepalive: true });
        });
      }
    });
  }

  static getInstance(): EventGatewayClient {
    if (!EventGatewayClient.instance) {
      EventGatewayClient.instance = new EventGatewayClient();
    }

    return EventGatewayClient.instance;
  }

  public initialize(origin: string) {
    try {
      new URL(origin);
    } catch {
      throw new Error('event gateway origin is not valid.');
    }

    this.origin = origin;

    this._start();

    this.sendEventMessages('GENERAL');
    this.sendEventMessages('PAYMENT');
  }

  public pushEvent({ eventName, eventType, eventParams, options }: EventData) {
    const deviceInfo = getDeviceInfo();
    const { browserName, browserVersion } = deviceInfo;

    if (browserName === 'safari' && browserVersion === '14.0.3') {
      //https://hpcnt.slack.com/archives/C02PLFTA5DL/p1666321514312989
      return;
    }

    const eventMessage = {
      event_type: eventType,
      event_name: eventName,
      event_timestamp: new Date().getTime(),
      event_uid: v4(),
      device_params: {
        os: typeof window !== 'undefined' && window.AzarJs ? 'WEB_VIEW' : 'WEB',
      },
      user_id: store.get(userDataAtom)?.userId || null,
      event_params: {
        path: window.location.pathname,
        unique_user_key: getDeviceId(),
        ...eventParams,
        ...deviceInfo,
      },
      service_code,
      unique_user_key: getDeviceId(),
      utm_params: { ...store.get(UTMParamsAtom) },
      ...options,
    } satisfies EventMessage;

    if (eventType === EVENT_TYPE.PAYMENT) {
      this.eventMessageQueue.PAYMENT.push(eventMessage);
    } else {
      this.eventMessageQueue.GENERAL.push(eventMessage);
    }
  }

  private async sendEventMessages(
    eventGatewayType: EventGatewayType,
    options?: { keepalive?: boolean }
  ) {
    if (this.origin == null) return;

    const eventMessages = this.eventMessageQueue[eventGatewayType].splice(0, 100);

    if (!eventMessages.length) {
      return;
    }

    const endpoint = EVENT_ENDPOINT_MAP[eventGatewayType];

    const requeueFailedMessages = (failType: 'not_ok' | 'error') => {
      const retryMessages: EventMessage[] = eventMessages.map((message) => ({
        ...message,
        event_params: {
          ...(message.event_params as Record<string, unknown>),
          event_gateway_send_fail_type: failType,
        },
      }));

      this.eventMessageQueue[eventGatewayType] = retryMessages.concat(
        this.eventMessageQueue[eventGatewayType]
      );
    };

    try {
      const response = await fetch(`${this.origin}${endpoint}batch`, {
        method: 'post',
        keepalive: options?.keepalive,
        credentials: 'omit',
        body: JSON.stringify(eventMessages),
        headers: {
          'Content-Type': 'application/json',
          'x.hpcnt.service.code': service_code,
        },
      });

      if (!response.ok) {
        requeueFailedMessages('not_ok');
        Sentry.captureMessage(`Event Gateway Response Not OK`, {
          extra: {
            response,
          },
        });
      }
    } catch (error) {
      requeueFailedMessages('error');
      Sentry.captureMessage('Event Gateway Send Failed', {
        extra: {
          error,
        },
      });
    }
  }
}

export const pushEvent = (eventData: EventData) => {
  const { eventName, eventType, options, eventParams } = eventData;
  const isPwa = isPwaMode();
  const { isMobile, os } = getDeviceInfo();
  const ABConfig = {
    ...(store.get(visitorConfigAtom) || {}),
  };

  const commonEventParams = {
    ...(isPwa ? { isPwa } : {}),
    ABConfig,
  };
  const webviewEventParams = safeWindow?.AzarJs
    ? {
        api_version: safeWindow.AzarJs.getApiVersion?.(),
      }
    : {};
  const eventParamsWithConfigs = {
    ...eventParams,
    ...commonEventParams,
    ...webviewEventParams,
  };

  const endpoint = EVENT_ENDPOINT_MAP[eventType === EVENT_TYPE.PAYMENT ? 'PAYMENT' : 'GENERAL'];

  window.dataLayer?.push({
    event: eventName,
    eventType,
    eventParams: {
      ...eventParamsWithConfigs,
      device_id: getDeviceId(),
      os,
      isMobile,
    },
    options: { ...options, device_id: getDeviceId() },
    url: endpoint,
  });

  if (DEBUG) {
    store.set(
      debugEventLogsAtom,
      [{ ...eventData, date: new Date().toISOString() }]
        .concat(store.get(debugEventLogsAtom))
        .slice(0, 100)
    );
  }

  EventGatewayClient.getInstance().pushEvent({ ...eventData, eventParams: eventParamsWithConfigs });
};

export const setEventGatewayOrigin = (origin: string) => {
  EventGatewayClient.getInstance().initialize(origin);
  initializeMetric(origin);
};
